import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useReactToPrint } from 'react-to-print';
import { getCompanyLogoFromSubdomainV2 } from 'src/services/account';
import Manifest, { ManifestRenderContainer } from './Manifest';

const usePrintManifest = () => {
  const [companyLogo, setCompanyLogo] = useState<string>('');

  const componentRef = useRef(null);
  const containerRef = useRef(null);

  const host = window.location.host;
  let subdomain = host.split('.')[0];
  if (subdomain === 'localhost:3000') {
    subdomain = process.env.NEXT_PUBLIC_DOMAIN!;
  }

  if (subdomain.includes('myportal')) {
    subdomain = subdomain.split('.')[0];
  }

  /**
   * Print element stored as containerRef
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleContainerPrint = useCallback(
    useReactToPrint({
      content: () => containerRef.current || null,
      documentTitle: 'Manifest',
      pageStyle: `
      @page {
        size: landscape;
      }
      table {
        border-color: red;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          font-family: Arial, sans-serif;
        }
        table {borderWidth: 0px;}
      }
    `,
      removeAfterPrint: true
    }),
    [containerRef]
  );

  /**
   * Print element stored as componentRef
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleComponentPrint = useCallback(
    useReactToPrint({
      content: () => componentRef.current || null,
      documentTitle: 'Manifest',
      pageStyle: `
      @page {
        size: landscape;
        margin: 20mm;
      }
      table {
        border-color: red;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          font-family: Arial, sans-serif;
        }
        table {borderWidth: 0px;}
      }
    `,
      removeAfterPrint: true
    }),
    [componentRef]
  );

  const printManifest = useCallback(() => {
    handleComponentPrint();
  }, [handleComponentPrint]);

  /**
   * Print manifest without preview.
   *
   * @param rows
   * @param cols
   */
  const manifestStraightToPrint = useCallback(
    (rows: any[], cols: any[], isNewGrid: boolean) => {
      let filteredColumns: any[] = [];
      if (isNewGrid) {
        // devextreme columns
        filteredColumns = cols.filter(col => col.caption !== undefined && col.caption.toLowerCase() !== 'actions');
      } else {
        // MUI DataGrid columns
        filteredColumns = cols.filter(
          col => col.field !== '__check__' && col.field !== '__index__' && col.field !== 'actions'
        );
      }

      const count = rows.length;
      const palletCount = rows.reduce((acc, row) => acc + Number(row.totalItems), 0);
      const totalWeight = rows.reduce((acc, row) => acc + Number(row.totalWeight), 0);
      const accountCodes = [
        ...new Set(rows.map(row => row.accountCode).filter(code => code && code.trim().length > 0))
      ].join(', ');

      const Component = (
        <Manifest
          ref={componentRef}
          rows={rows}
          columns={filteredColumns}
          totalCons={count}
          totalPallets={palletCount}
          totalWeight={totalWeight}
          logo={companyLogo}
          isNewGrid={isNewGrid}
          accountCodes={accountCodes}
        />
      );
      if (containerRef.current) {
        (containerRef.current as HTMLElement).innerHTML = ReactDOMServer?.renderToString?.(Component);
        handleContainerPrint();
      }
    },
    [companyLogo, handleContainerPrint]
  );

  /**
   * Preview manifest in a new window with options to print and download as PDF
   *
   * NOT SURE THIS IS NEEDED?
   * @param rows
   * @param cols
   */
  // const manifestPrintPreview = (rows: any[], cols: any[]) => {
  //   const filteredColumns = cols.filter(
  //     col => col.field !== '__check__' && col.field !== '__index__' && col.field !== 'actions'
  //   );

  //   const count = rows.length;
  //   const palletCount = rows.reduce((acc, row) => acc + Number(row.totalItems), 0);
  //   const totalWeight = rows.reduce((acc, row) => acc + Number(row.totalWeight), 0);

  //   // Download manifest as PDF
  //   const downloadManifest = (window: Window) => {
  //     const printable = window.document.getElementById('printableTable');
  //     if (printable) {
  //       html2canvas(printable).then(canvas => {
  //         const imgData = canvas.toDataURL('image/png');
  //         const pdf = new jsPDF('landscape', 'mm', 'a4');

  //         const pageWidth = 297;
  //         const imageWidth = canvas.width;
  //         const imageHeight = canvas.height;

  //         const height = pageWidth * (imageHeight / imageWidth);

  //         pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, height);
  //         pdf.save('manifest.pdf');
  //       });
  //     }
  //   };

  //   // Convert to a string
  //   const htmlString = ReactDOMServer.renderToString(
  //     <div style={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
  //       <style>
  //         {`
  //         @media print {
  //           #printBtn {
  //             display: none;
  //           }
  //           #downloadBtn {
  //             display: none;
  //           }
  //         }
  //       `}
  //       </style>
  //       <div style={{ display: 'flex', gap: '20px' }}>
  //         <button id="printBtn" style={{ alignSelf: 'flex-start' }}>
  //           Print
  //         </button>
  //         <button id="downloadBtn" style={{ alignSelf: 'flex-start' }}>
  //           Download as PDF
  //         </button>
  //       </div>

  //       <Manifest
  //         ref={componentRef}
  //         rows={rows}
  //         columns={filteredColumns}
  //         totalCons={count}
  //         totalPallets={palletCount}
  //         totalWeight={totalWeight}
  //         logo={companyLogo}
  //       />
  //     </div>
  //   );

  //   const printWindow = window.open('', '_blank');
  //   if (printWindow) {
  //     printWindow.document.write(htmlString);
  //     printWindow.document.title = 'Manifest Preview';
  //     printWindow.document.close();

  //     const printButton = printWindow.document.querySelector('button');
  //     if (printButton) {
  //       printButton.onclick = function () {
  //         printWindow.print();
  //       };
  //     }

  //     const downloadButton = printWindow.document.querySelector('#downloadBtn');
  //     if (downloadButton) {
  //       (downloadButton as HTMLButtonElement).onclick = function () {
  //         downloadManifest(printWindow);
  //       };
  //     }
  //   }
  // };

  useEffect(() => {
    getCompanyLogoFromSubdomainV2({ subdomain: subdomain })
      .then(res => {
        const { logo } = res.data;
        if (logo) {
          setCompanyLogo(logo);
        }
      })
      .catch(() => {
        setCompanyLogo('');
      });
  }, [subdomain]);

  return useMemo(
    () => ({
      printManifest,
      ManifestPrinter: Manifest,
      ref: componentRef,
      containerRef,
      ManifestRenderContainer,
      manifestStraightToPrint
    }),
    [printManifest, componentRef, containerRef, manifestStraightToPrint]
  );
};

export default usePrintManifest;
